import React from 'react';
import { Typography, ConfigProvider } from 'antd';
import useDefaultTrebbleAntDTokenConfig from '../helper/useDefaultTrebbleAntDTokenConfig';

const { Title: AntTitle, Text: AntText, Paragraph: AntParagraph } = Typography;

interface TrebbleTypographyProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  disableDefaultColors?: boolean;
}

interface TrebbleTitleProps extends TrebbleTypographyProps {
  level?: 1 | 2 | 3 | 4 | 5;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
}

interface TrebbleTextProps extends TrebbleTypographyProps {
  strong?: boolean;
  type?: 'secondary' | 'success' | 'warning' | 'danger';
  size?: 'small' | 'medium' | 'large';
}

interface TrebbleParagraphProps extends TrebbleTypographyProps {
  size?: 'small' | 'medium' | 'large';
}

// Title Component
const TrebbleTitle: React.FC<TrebbleTitleProps> = ({ 
  children, 
  className = '', 
  level = 3,
  type,
  style,
  disableDefaultColors = false,
  ...props 
}) => {
  const tokenConfig = useDefaultTrebbleAntDTokenConfig();
  
  // Only apply default colors if not disabled
  const defaultColors = disableDefaultColors ? '' : 'text-gray-900 dark:text-gray-100';
  const combinedClassName = `${defaultColors} font-black ${className}`.trim();
  const combinedStyle = { fontWeight: 900, ...style };
  
  return (
    <ConfigProvider theme={tokenConfig}>
      <AntTitle 
        level={level}
        type={type}
        className={combinedClassName}
        style={combinedStyle}
        {...props}
      >
        {children}
      </AntTitle>
    </ConfigProvider>
  );
};

// Text Component  
const TrebbleText: React.FC<TrebbleTextProps> = ({
  children,
  className = '',
  strong = false,
  type,
  size = 'medium',
  style,
  disableDefaultColors = false,
  ...props
}) => {
  const tokenConfig = useDefaultTrebbleAntDTokenConfig();
  
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-base', 
    large: 'text-lg'
  };
  
  // Only apply default colors if not disabled
  const defaultColors = disableDefaultColors ? '' : 'text-gray-900 dark:text-gray-100';
  const combinedClassName = `${defaultColors} ${sizeClasses[size]} ${className}`.trim();
  
  return (
    <ConfigProvider theme={tokenConfig}>
      <AntText
        strong={strong}
        type={type}
        className={combinedClassName}
        style={style}
        {...props}
      >
        {children}
      </AntText>
    </ConfigProvider>
  );
};

// Paragraph Component
const TrebbleParagraph: React.FC<TrebbleParagraphProps> = ({
  children,
  className = '',
  size = 'medium',
  style,
  disableDefaultColors = false,
  ...props
}) => {
  const tokenConfig = useDefaultTrebbleAntDTokenConfig();
  
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };
  
  // Only apply default colors if not disabled
  const defaultColors = disableDefaultColors ? '' : 'text-gray-900 dark:text-gray-100';
  const combinedClassName = `${defaultColors} ${sizeClasses[size]} ${className}`.trim();
  
  return (
    <ConfigProvider theme={tokenConfig}>
      <AntParagraph
        className={combinedClassName}
        style={style}
        {...props}
      >
        {children}
      </AntParagraph>
    </ConfigProvider>
  );
};

// Export individual components
export { TrebbleTitle, TrebbleText, TrebbleParagraph };

// Export as a compound component
const TrebbleTypography = {
  Title: TrebbleTitle,
  Text: TrebbleText,
  Paragraph: TrebbleParagraph
};

export default TrebbleTypography; 